import type { BoxProps } from "@highoutput/ui-components";
import { Box, keyframes, useBreakpoint, usePrefersReducedMotion } from "@highoutput/ui-components";
import Image from "next/image";
import For from "../../utilities/for";

export default function Orbit(props: Omit<BoxProps, "children">) {
  return (
    <Box
      width={{
        base: "316px",
        lg: "411px",
        xl: "437px",
      }}
      height={{
        base: "316px",
        lg: "411px",
        xl: "437px",
      }}
      flexShrink={0}
      flexGrow={0}
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...props}
    >
      <Box
        position="absolute"
        border="1px solid"
        borderColor="#F9D209"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        rounded="full"
        width="full"
        height="full"
      >
        <Box position="relative" width="full" height="full">
          <Images />
          <Dots />
        </Box>
      </Box>

      <Highlight />
    </Box>
  );
}

function Highlight() {
  return (
    <Box
      position="relative"
      width={{
        base: "255px",
        lg: "332px",
        xl: "352px",
      }}
      height={{
        base: "255px",
        lg: "332px",
        xl: "352px",
      }}
      rounded="full"
      overflow="hidden"
    >
      <Image
        src="/images/hero/ladies-on-laptop.webp"
        alt=""
        width={400}
        height={400}
        priority
        quality={90}
      />
    </Box>
  );
}

// TEST

const keyframeSpin1 = keyframes`100% { transform: rotate(360deg); }`;
const keyframeSpin2 = keyframes`100% { transform: rotate(-360deg); }`;
const keyframeSpin3 = keyframes`100% { transform: rotate(405deg); }`;

function Images() {
  const prefersReducedMotion = usePrefersReducedMotion();

  const parentAnimation = !prefersReducedMotion
    ? `${keyframeSpin1} infinite 20s linear`
    : undefined;

  const childrenAnimation = !prefersReducedMotion
    ? `${keyframeSpin2} infinite 20s linear`
    : undefined;

  const breakpoint = useBreakpoint();

  return (
    <Box
      position="absolute"
      width="full"
      height="full"
      top={0}
      left={0}
      animation={parentAnimation}
    >
      <For
        each={[
          "/images/hero/alaiza.webp",
          "/images/hero/jassy.webp",
          "/images/hero/niel.webp",
          "/images/hero/angelica.webp",
        ]}
      >
        {(src, index) => (
          <Box
            position="absolute"
            rounded="full"
            overflow="hidden"
            sx={getOrbitPos({
              index,
              baseSize: ["base", "sm", "md"].includes(breakpoint) ? 28 : 37,
            })}
          >
            <Box
              position="relative"
              width={{
                base: "28px",
                lg: "37px",
              }}
              height={{
                base: "28px",
                lg: "37px",
              }}
              animation={childrenAnimation}
            >
              <Image src={src} alt="" width={40} height={40} />
            </Box>
          </Box>
        )}
      </For>
    </Box>
  );
}

function Dots() {
  const prefersReducedMotion = usePrefersReducedMotion();

  const animation = !prefersReducedMotion ? `${keyframeSpin3} infinite 20s linear` : undefined;

  const breakpoint = useBreakpoint();

  return (
    <Box
      position="absolute"
      width="full"
      height="full"
      top={0}
      left={0}
      transform="rotate(45deg)"
      animation={animation}
    >
      <For each={new Array(4).fill(null)}>
        {(_, index) => (
          <Box
            position="absolute"
            width={{
              base: "12px",
              lg: "16px",
              xl: "18px",
            }}
            height={{
              base: "12px",
              lg: "16px",
              xl: "18px",
            }}
            bgColor="#F9D209"
            rounded="full"
            transform="rotate(90deg)"
            sx={getOrbitPos({
              index,
              baseSize: breakpoint === "base" ? 12 : breakpoint === "lg" ? 16 : 18,
            })}
          />
        )}
      </For>
    </Box>
  );
}

function getOrbitPos({ index, baseSize }: { index: number; baseSize: number }) {
  const halfSize = `${(baseSize / 2) * -1}px`;

  return {
    "0": {
      top: 0,
      left: "50%",
      transform: "translateX(-50%)",
      marginTop: halfSize,
    },
    "1": {
      right: 0,
      top: "50%",
      transform: "translateY(-50%)",
      marginRight: halfSize,
    },
    "2": {
      bottom: 0,
      left: "50%",
      transform: "translateX(-50%)",
      marginBottom: halfSize,
    },
    "3": {
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      marginLeft: halfSize,
    },
  }[index.toString()];
}
